import * as React from "react";

import { getConversation, listIncomingConversations } from "@/api/client";
import {
  AgentInfo,
  ConversationView,
  IncomingConversationView,
  NegotiationStatus,
  UserEvent,
} from "@/api/types";

/**
 * Replacement for the retired per-user SSE feed. Instead of a long-lived
 * {@code text/event-stream} (which API Gateway's 30s integration timeout kills
 * in the cloud), this hook polls two REST snapshots and diffs successive
 * responses into the SAME {@link UserEvent} stream the homepage already
 * consumes — so the event-handling logic in ChatPage is untouched.
 *
 * <p>Two things are polled:</p>
 * <ul>
 *   <li>{@code GET /conversations/incoming} — always; detects new inbound
 *       negotiations (→ ConversationInvited) and their outcomes.</li>
 *   <li>{@code GET /conversations/{activeConversationId}} — only while an
 *       outgoing conversation is live; drives round / participant-state /
 *       terminal narration for the wire panel.</li>
 * </ul>
 *
 * <p><b>Cadence (adaptive):</b> 20s idle, dropping to 5s for a 60s window
 * after any detected change (or an explicit {@link PollingControls.pokeNow}).
 * Paused while the tab is hidden; an immediate poll fires on return to
 * foreground. Call {@code pokeNow()} right after sending a message so the wire
 * lights up without waiting for the next tick.</p>
 */

const num = (v: string | undefined, fallback: number): number => {
  const n = v ? Number(v) : NaN;
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

const IDLE_MS = num(import.meta.env.VITE_POLL_IDLE_MS, 20_000);
const ACTIVE_MS = num(import.meta.env.VITE_POLL_ACTIVE_MS, 5_000);
const ACTIVE_WINDOW_MS = num(import.meta.env.VITE_POLL_ACTIVE_WINDOW_MS, 60_000);
/** Consecutive poll failures before we surface the "updates delayed" hint. */
const FAIL_THRESHOLD = 2;

const TERMINAL: NegotiationStatus[] = ["ACCEPTED", "FAILED", "EXPIRED", "CANCELLED"];
const isTerminal = (s: NegotiationStatus) => TERMINAL.includes(s);

/** Map a ConversationView participant onto the AgentInfo shape SSE events used. */
function toAgentInfo(p: ConversationView["participants"][number]): AgentInfo {
  return { id: p.agent_id, displayName: p.display_name, handle: null };
}

/** A neutral reason string for a non-accepted terminal state (SSE carried one; snapshots don't). */
function reasonFor(status: NegotiationStatus): string {
  switch (status) {
    case "EXPIRED":
      return "the request expired";
    case "CANCELLED":
      return "the meeting was cancelled";
    default:
      return "no agreement could be reached";
  }
}

/**
 * Best-effort slot count for a counter: the offered slots on the participant's
 * most recent {@code counter_propose} wire message. Empty when the payload
 * isn't recognisable — describeEvent phrases that gracefully.
 */
function counterSlots(
  cv: ConversationView,
  agentId: string,
): { start: string; end: string }[] {
  const counters = cv.messages.filter(
    (m) => m.message_type === "counter_propose" && m.from_agent === agentId,
  );
  const last = counters[counters.length - 1];
  const payload = last?.payload as { slots?: unknown } | undefined;
  const slots = Array.isArray(payload?.slots) ? payload?.slots : [];
  return (slots as { start?: string; end?: string }[])
    .filter((s) => s && s.start && s.end)
    .map((s) => ({ start: s.start as string, end: s.end as string }));
}

/** Per-active-conversation diff cursor. */
type ActiveCursor = {
  convId: string;
  round: number;
  states: Map<string, string>;
  status: NegotiationStatus;
};

export interface PollingControls {
  /** Force an immediate poll and arm the fast (5s) cadence window. */
  pokeNow: () => void;
}

export function usePolling(
  onEvent: (event: UserEvent, seq: number) => void,
  onError: (err: unknown) => void,
  onCaughtUp: () => void,
  activeConversationId: string | null,
): PollingControls {
  const onEventRef = React.useRef(onEvent);
  const onErrorRef = React.useRef(onError);
  const onCaughtUpRef = React.useRef(onCaughtUp);
  const activeIdRef = React.useRef(activeConversationId);
  onEventRef.current = onEvent;
  onErrorRef.current = onError;
  onCaughtUpRef.current = onCaughtUp;
  activeIdRef.current = activeConversationId;

  // The imperative poke handle is stable; the effect wires it up on mount.
  const pokeRef = React.useRef<() => void>(() => {});
  const poke = React.useCallback(() => pokeRef.current(), []);

  // Diff cursors, seeded on the first poll. Held in refs (not effect-locals) so
  // they SURVIVE effect re-runs — React StrictMode double-invokes effects in
  // dev, and without this each re-setup would start with an empty prevIncoming
  // and re-emit ConversationInvited for conversations already shown (duplicate
  // "negotiating on your behalf" bubbles). Reset only on a real component remount.
  const seededRef = React.useRef(false);
  const prevIncomingRef = React.useRef(new Map<string, NegotiationStatus>());
  const prevActiveRef = React.useRef<ActiveCursor | null>(null);

  React.useEffect(() => {
    let stopped = false;
    let timer: number | undefined;
    let activeUntil = 0; // ms epoch; while now < activeUntil we poll fast
    let failures = 0;
    let inFlight = false;
    let lastStart = 0;

    const prevIncoming = prevIncomingRef.current;
    const emit = (event: UserEvent) => onEventRef.current(event, 0);

    // Emit the correct terminal event for a closed conversation. On the first
    // (seeding) poll we don't know / need the decided slot — the event is
    // suppressed by ChatPage until caught_up — so we skip the detail fetch.
    const emitTerminalForIncoming = async (
      c: IncomingConversationView,
      withDetail: boolean,
    ) => {
      const convId = c.conversation_id;
      if (c.status === "ACCEPTED") {
        let slot = { start: "", end: "" };
        if (withDetail) {
          try {
            const cv = await getConversation(convId);
            if (cv.decided_slot) slot = cv.decided_slot;
          } catch {
            /* fall back to the empty slot; toast still fires, range blank */
          }
        }
        emit({ type: "SlotAccepted", data: { conversationId: convId, slot } });
      } else if (c.superseded_by_reschedule) {
        // Cancelled only because a reschedule of it was accepted — the meeting
        // moved. Stay silent: raising "couldn't reach agreement" here is what
        // paired a scary red toast with the green "agreement reached" of the
        // successor. The successor's SlotAccepted announces the new time.
        return;
      } else {
        emit({
          type: "NegotiationFailed",
          data: { conversationId: convId, reason: reasonFor(c.status) },
        });
      }
    };

    const pollIncoming = async (firstTime: boolean): Promise<boolean> => {
      const rows = await listIncomingConversations();
      const next = new Map<string, NegotiationStatus>();
      let changed = false;
      for (const c of rows) {
        next.set(c.conversation_id, c.status);
        const prev = prevIncoming.get(c.conversation_id);
        if (prev === undefined) {
          emit({
            type: "ConversationInvited",
            data: {
              conversationId: c.conversation_id,
              initiator: c.initiator,
              participants: c.participants,
            },
          });
          if (isTerminal(c.status)) await emitTerminalForIncoming(c, !firstTime);
          changed = true;
        } else if (prev !== c.status && isTerminal(c.status)) {
          await emitTerminalForIncoming(c, !firstTime);
          changed = true;
        }
      }
      prevIncoming.clear();
      for (const [k, v] of next) prevIncoming.set(k, v);
      return changed;
    };

    const emitActiveTerminal = (cv: ConversationView) => {
      if (cv.status === "ACCEPTED" && cv.decided_slot) {
        emit({
          type: "SlotAccepted",
          data: { conversationId: cv.id, slot: cv.decided_slot },
        });
      } else {
        emit({
          type: "NegotiationFailed",
          data: { conversationId: cv.id, reason: reasonFor(cv.status) },
        });
      }
    };

    const diffActive = (cv: ConversationView): boolean => {
      const states = new Map(cv.participants.map((p) => [p.agent_id, p.state]));
      let changed = false;
      const prevActive = prevActiveRef.current;
      const fresh = !prevActive || prevActive.convId !== cv.id;

      if (fresh) {
        emit({
          type: "NegotiationOpened",
          data: {
            conversationId: cv.id,
            participants: cv.participants.map(toAgentInfo),
          },
        });
        if (cv.current_round >= 1) {
          emit({
            type: "RoundAdvanced",
            data: { conversationId: cv.id, round: cv.current_round },
          });
        }
        for (const p of cv.participants) {
          if (p.state === "COUNTERED") {
            emit({
              type: "CounterReceived",
              data: {
                conversationId: cv.id,
                from: toAgentInfo(p),
                slots: counterSlots(cv, p.agent_id),
              },
            });
          }
        }
        if (isTerminal(cv.status)) emitActiveTerminal(cv);
        changed = true;
      } else {
        const prev = prevActive as ActiveCursor;
        if (cv.current_round > prev.round) {
          emit({
            type: "RoundAdvanced",
            data: { conversationId: cv.id, round: cv.current_round },
          });
          changed = true;
        }
        for (const p of cv.participants) {
          if (p.state === "COUNTERED" && prev.states.get(p.agent_id) !== "COUNTERED") {
            emit({
              type: "CounterReceived",
              data: {
                conversationId: cv.id,
                from: toAgentInfo(p),
                slots: counterSlots(cv, p.agent_id),
              },
            });
            changed = true;
          }
        }
        if (isTerminal(cv.status) && !isTerminal(prev.status)) {
          emitActiveTerminal(cv);
          changed = true;
        }
      }
      prevActiveRef.current = { convId: cv.id, round: cv.current_round, states, status: cv.status };
      return changed;
    };

    const pollOnce = async (): Promise<boolean> => {
      const firstTime = !seededRef.current;
      let changed = await pollIncoming(firstTime);

      const activeId = activeIdRef.current;
      if (activeId) {
        try {
          const cv = await getConversation(activeId);
          changed = diffActive(cv) || changed;
        } catch {
          // Conversation may not be queryable yet right after opening; retry next tick.
        }
      } else {
        prevActiveRef.current = null;
      }

      if (firstTime) {
        // Baseline emitted above ran with caught_up=false (silent). Announce the
        // boundary now so subsequent diffs narrate — and don't treat the seed
        // itself as "activity" that would arm the fast window.
        seededRef.current = true;
        onCaughtUpRef.current();
        return false;
      }
      return changed;
    };

    const scheduleNext = () => {
      if (stopped || document.hidden) return;
      const delay = Date.now() < activeUntil ? ACTIVE_MS : IDLE_MS;
      timer = window.setTimeout(runTick, delay);
    };

    const runTick = async () => {
      if (stopped || document.hidden || inFlight) return;
      inFlight = true;
      lastStart = Date.now();
      try {
        const changed = await pollOnce();
        failures = 0;
        onErrorRef.current(null);
        if (changed) activeUntil = Date.now() + ACTIVE_WINDOW_MS;
      } catch (err) {
        failures += 1;
        if (failures >= FAIL_THRESHOLD) onErrorRef.current(err);
      } finally {
        inFlight = false;
        scheduleNext();
      }
    };

    // Force an immediate poll (e.g. right after the user sends a message).
    pokeRef.current = () => {
      if (stopped || document.hidden) return;
      activeUntil = Date.now() + ACTIVE_WINDOW_MS;
      window.clearTimeout(timer);
      void runTick();
    };

    // Re-poll immediately when the tab comes back to the foreground, but not if
    // we already polled within the last second (focus + visibility can co-fire).
    const onWake = () => {
      if (stopped || document.hidden) return;
      window.clearTimeout(timer);
      if (Date.now() - lastStart < 1_000) {
        scheduleNext();
      } else {
        void runTick();
      }
    };
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);

    void runTick();

    return () => {
      stopped = true;
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
    };
    // Mount-once: all live inputs are read through refs so re-renders don't
    // tear down the polling loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { pokeNow: poke };
}
